import { StopTimeType, StopType } from "types/MultiStop";

type ScheduleStopLike = Record<string, unknown> & {
  stop_type?: number;
  stop_time_type?: number;
};

type ScheduleLike = Record<string, unknown> & {
  stops?: ScheduleStopLike[];
};

function normalizeClassicTime(time: unknown): string | undefined {
  if (time === undefined || time === null || time === "") return undefined;
  const timeStr = String(time);
  const match = timeStr.match(/^(\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : timeStr;
}

function assignIfDefined(target: Record<string, unknown>, key: string, value: unknown) {
  if (value !== undefined && value !== null && value !== "") {
    target[key] = value;
  }
}

function mapStopTimeTypeToCargoReady(stopTimeType: unknown, isPickup: boolean): string | undefined {
  const type = typeof stopTimeType === "string" ? Number(stopTimeType) : stopTimeType;

  if (type === StopTimeType.BETWEEN) return "between";
  if (isPickup) {
    if (type === StopTimeType.AFTER) return "after";
    return undefined;
  }

  if (type === StopTimeType.BEFORE) return "before";
  if (type === StopTimeType.ASAP_DELIVERY) return "asap_delivery";
  return undefined;
}

function findDeliveryStop(stops: ScheduleStopLike[]): ScheduleStopLike | undefined {
  const dropoff = stops.find((stop) => stop.stop_type === StopType.DROPOFF);
  return dropoff ?? stops[1];
}

function buildFromFlat(schedule: ScheduleLike): Record<string, unknown> {
  const pickupCargoReady = (schedule.pickup_cargo_ready as string) || "after";
  const deliveryCargoReady = (schedule.delivery_cargo_ready as string) || "before";
  const modal: Record<string, unknown> = {
    pickup_cargo_ready: pickupCargoReady,
    delivery_cargo_ready: deliveryCargoReady,
  };

  assignIfDefined(modal, "pickup_ready_date", schedule.pickup_ready_date ?? schedule.pickup_after_date);
  assignIfDefined(modal, "pickup_ready_time", normalizeClassicTime(schedule.pickup_ready_time ?? schedule.pickup_after_time));
  // Modal only shows pickup_before_* for "between"
  if (pickupCargoReady === "between") {
    assignIfDefined(modal, "pickup_before_date", schedule.pickup_before_date);
    assignIfDefined(modal, "pickup_before_time", normalizeClassicTime(schedule.pickup_before_time));
  }
  assignIfDefined(modal, "pickup_hours_from", schedule.pickup_hours_from);
  assignIfDefined(modal, "pickup_hours_to", schedule.pickup_hours_to);
  assignIfDefined(modal, "pickup_timezone", schedule.pickup_timezone);

  // Modal primary delivery date field is always stop1_after_* (even for "before")
  const primaryDeliveryDate =
    deliveryCargoReady === "before"
      ? schedule.stop1_before_date ?? schedule.stop1_after_date ?? schedule.stop1_date
      : schedule.stop1_after_date ?? schedule.stop1_date;
  const primaryDeliveryTime =
    deliveryCargoReady === "before"
      ? schedule.stop1_before_time ?? schedule.stop1_after_time ?? schedule.stop1_time
      : schedule.stop1_after_time ?? schedule.stop1_time;

  if (deliveryCargoReady !== "asap_delivery") {
    assignIfDefined(modal, "stop1_after_date", primaryDeliveryDate);
    assignIfDefined(modal, "stop1_after_time", normalizeClassicTime(primaryDeliveryTime));
    if (deliveryCargoReady === "between") {
      assignIfDefined(modal, "stop1_before_date", schedule.stop1_before_date);
      assignIfDefined(modal, "stop1_before_time", normalizeClassicTime(schedule.stop1_before_time));
    }
    assignIfDefined(modal, "stop1_timezone", schedule.stop1_timezone);
  }

  assignIfDefined(modal, "delivery_hours_from", schedule.delivery_hours_from);
  assignIfDefined(modal, "delivery_hours_to", schedule.delivery_hours_to);

  return modal;
}

function buildFromStops(schedule: ScheduleLike, stops: ScheduleStopLike[]): Record<string, unknown> {
  const pickup = stops[0];
  const delivery = findDeliveryStop(stops);
  const modal: Record<string, unknown> = {};

  const pickupCargoReady =
    mapStopTimeTypeToCargoReady(pickup?.stop_time_type, true) ||
    (typeof schedule.pickup_cargo_ready === "string" ? schedule.pickup_cargo_ready : undefined) ||
    "after";
  const deliveryCargoReady =
    mapStopTimeTypeToCargoReady(delivery?.stop_time_type, false) ||
    (typeof schedule.delivery_cargo_ready === "string" ? schedule.delivery_cargo_ready : undefined) ||
    "before";

  modal.pickup_cargo_ready = pickupCargoReady;
  modal.delivery_cargo_ready = deliveryCargoReady;

  assignIfDefined(modal, "pickup_ready_date", pickup?.stop_after_date ?? schedule.pickup_ready_date ?? schedule.pickup_after_date);
  assignIfDefined(
    modal,
    "pickup_ready_time",
    normalizeClassicTime(pickup?.stop_after_time ?? schedule.pickup_ready_time ?? schedule.pickup_after_time),
  );
  // Modal only shows pickup_before_* for "between"
  if (pickupCargoReady === "between") {
    assignIfDefined(modal, "pickup_before_date", pickup?.stop_before_date ?? schedule.pickup_before_date);
    assignIfDefined(modal, "pickup_before_time", normalizeClassicTime(pickup?.stop_before_time ?? schedule.pickup_before_time));
  }
  assignIfDefined(modal, "pickup_hours_from", pickup?.stop_hours_start ?? schedule.pickup_hours_from);
  assignIfDefined(modal, "pickup_hours_to", pickup?.stop_hours_end ?? schedule.pickup_hours_to);
  assignIfDefined(modal, "pickup_timezone", pickup?.stop_timezone ?? schedule.pickup_timezone);

  if (deliveryCargoReady !== "asap_delivery") {
    // Modal primary field is stop1_after_* even for "before"; RFV2 Before UI uses stop_before_*
    const primaryDeliveryDate =
      deliveryCargoReady === "before"
        ? delivery?.stop_before_date ?? delivery?.stop_after_date ?? schedule.stop1_before_date ?? schedule.stop1_after_date ?? schedule.stop1_date
        : delivery?.stop_after_date ?? schedule.stop1_after_date ?? schedule.stop1_date;
    const primaryDeliveryTime =
      deliveryCargoReady === "before"
        ? delivery?.stop_before_time ?? delivery?.stop_after_time ?? schedule.stop1_before_time ?? schedule.stop1_after_time ?? schedule.stop1_time
        : delivery?.stop_after_time ?? schedule.stop1_after_time ?? schedule.stop1_time;

    assignIfDefined(modal, "stop1_after_date", primaryDeliveryDate);
    assignIfDefined(modal, "stop1_after_time", normalizeClassicTime(primaryDeliveryTime));
    if (deliveryCargoReady === "between") {
      assignIfDefined(modal, "stop1_before_date", delivery?.stop_before_date ?? schedule.stop1_before_date);
      assignIfDefined(modal, "stop1_before_time", normalizeClassicTime(delivery?.stop_before_time ?? schedule.stop1_before_time));
    }
    assignIfDefined(modal, "stop1_timezone", delivery?.stop_timezone ?? schedule.stop1_timezone);
  }

  assignIfDefined(modal, "delivery_hours_from", delivery?.stop_hours_start ?? schedule.delivery_hours_from);
  assignIfDefined(modal, "delivery_hours_to", delivery?.stop_hours_end ?? schedule.delivery_hours_to);

  return modal;
}

/**
 * Prefill Load Template modal flat planning fields from the duplicated template.
 * Prefer stops[] stop_time_type / dates when present so Confirm does not overwrite
 * template planning types with hardcoded after/before defaults.
 */
export function buildLoadTemplateModalSchedule(schedule?: ScheduleLike): Record<string, unknown> {
  if (!schedule) {
    return {
      pickup_cargo_ready: "after",
      delivery_cargo_ready: "before",
    };
  }

  const { stops } = schedule;
  if (Array.isArray(stops) && stops.length >= 2) {
    return buildFromStops(schedule, stops);
  }

  return buildFromFlat(schedule);
}
